/** The live world transport — ONE WebSocket, two lanes (STACK D21 / `airsec/api/protocol.py`).
 *
 * Three laws this file exists to enforce:
 *
 *  1. **Zero React in the animation loop.** Hot-lane positions never touch React state.
 *     `Track` objects are stable and mutated in place by one module-level rAF loop, so a
 *     renderer reads `track.x` at 60fps with no allocation, no reconciliation, no hooks.
 *  2. **<=10Hz for everything React sees.** Structure (counts/zones/flags/selection) is
 *     published through `useSyncExternalStore` on a throttled, referentially-stable object,
 *     so an unchanged store cannot re-render a panel.
 *  3. **Decimation is the backend's job.** We subscribe with a viewport + altitude and the
 *     server sends a capped set (ORACLE: "the frontend must never receive the firehose").
 *
 * Altitude note (verified against `airsec/core/entities.py::snapshot`): `facility` returns
 * only gid/kind/x/y — no `state`, so `F_LOST` can never be set, and no `cameras`, which the
 * signature interaction ("which cameras see the selected target") needs. `zone` carries both
 * and is capped identically at 500 (`world.ALTITUDES`). `zone` is therefore the warroom default.
 */

import { useSyncExternalStore } from 'react'
import { isStatic, loadRecording, setReplayClock } from './static.ts'
import { unpack } from 'msgpackr'
// F_SELECTED is deliberately NOT imported here: it is decoded into `Track.flags` like every
// other flag, but it is this connection's own echo and is never what decides `Track.selected`
// (see `isSelectedTrack`). The wire gate is where the echo gets asserted.
import {
  F_ABANDONED, F_CARRIED, F_KIND, F_LOST, F_PLACED,
  KINDS, STRIDE, TAG_CTRL, TAG_HOT,
} from './types.ts'
import type { Altitude, Counts, Flag, Track, Viewport, WorldSnapshot } from './types.ts'

/** Interpolation state, carried on the same object so the hot loop allocates nothing. */
interface Live extends Track {
  /** Interpolation origin (where we were when this frame landed). */
  px: number
  py: number
  /** Interpolation target (where the server says we are). */
  tx: number
  ty: number
  /** performance.now() when the target was set, and the span to reach it. */
  t0: number
  span: number
}

// rail history cap; evicted findings refetch via `flagsSince()` in lib/api.ts
// (GET /demo/flags), NOT /history/events — that lane carries world edges only
// (`EventType` has no flag member), never a graded Flag.
const MAX_FLAGS = 500
const PUBLISH_MS = 100      // law 2: <=10Hz to React
const STALE_MS = 3000       // no hot frame for this long => the producer stopped, not the socket
const RETRY_MIN = 400
const RETRY_MAX = 8000

// rAF is absent under Node (the wire-verification harness imports this module directly).
const nextFrame: (cb: (t: number) => void) => unknown =
  typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame
    : (cb) => setTimeout(() => cb(performance.now()), 16)

// ---------------------------------------------------------------- state

const tracks = new Map<number, Live>()
const gids = new Map<number, string>()
const camerasById = new Map<number, string[]>()  // tracker-observed cameras, control-lane fed
const attrsById = new Map<number, Record<string, unknown>>()  // producer attrs (boxes, staff, …)
const seen = new Set<number>()

const trackSubs = new Set<(tracks: Map<number, Track>, t: number) => void>()
const storeSubs = new Set<() => void>()

let sock: WebSocket | null = null
let wsUrl = ''
let attempt = 0
let retryTimer: ReturnType<typeof setTimeout> | null = null
let closed = false          // true only after disconnectWorld()

let viewport: Viewport | null = null
let altitude: Altitude = 'zone'
// Selection is a SET, and it is keyed by gid, because the gid is what the SERVER selects on
// (`app.py::_apply_subscribe` -> `sub.selected = set(payload['selected_ids'])`, and
// `pack_hot(..., selected)` tests `d["gid"] in selected`). The server has always taken a set
// and ranks every pinned entity at 0 in decimation (`world.get_snapshot(pin=...)`); a single
// `selected: number|null` was a CLIENT ceiling, not a protocol one. VISION ¶1 asks for
// "hunderds of people that i want to track and highlight at once" — this is that set.
//
// `selIds` is the hot-lane mirror: wire ids selected before the control lane has named them
// (a `select(id)` from a click on a ping whose gid has not arrived). Ids are per-connection
// transport detail and are dropped on close; `selGids` is the reconnect-durable identity.
const selGids = new Set<string>()
const selIds = new Set<number>()
// The ANCHOR is the last target added, and it is what every single-target consumer reads
// (`selectedGid()`, `useSelected()`, the dossier, the wall's amber frames). Keeping an anchor
// is what lets the selection grow to hundreds without any of those panels changing: they ask
// "which one are we looking at", the map and the wire ask "which ones are lit".
let anchorGid: string | null = null
let anchorId: number | null = null

let lastHot = 0
let looping = false

const EMPTY_COUNTS: Counts = {
  persons: 0, items: 0, groups: 0, unattended_items: 0, total: 0, returned: 0,
}

/** Mutable draft. Never handed to React — `world` is the published, frozen-by-convention copy. */
let draft: WorldSnapshot = {
  counts: EMPTY_COUNTS, zones: {}, t: 0, connected: false, stale: false,
  altitude, truncated: false, selected: null, selectedCount: 0,
}
let world: WorldSnapshot = draft
let flags: Flag[] = []
let pubFlags: Flag[] = flags
// Referentially-stable published selection for `useSelection()`. Rebuilt only when the set
// actually changes, so a panel reading it cannot re-render on an unrelated publish.
let pubSelection: readonly string[] = []

let publishTimer: ReturnType<typeof setTimeout> | null = null
let lastPublish = 0

// RETIRED 2026-08-08 — `SELECTED_REFRESH_MS`, the 2 s selected-target re-subscribe.
//
// It existed because a CONTINUING track's `cameras` list only refreshed on a full snapshot,
// so without a periodic re-subscribe a walking selected target kept the camera set it had at
// selection time — and that set drives the map's sightlines AND the wall's amber frames. The
// note here called itself a stopgap and said to retire it "if the backend ever emits
// cameras-changed deltas". It does now.
//
// The root cause was never lossy selection state: the `upd` lane carried `cameras` all along
// but was GUARDED on `attrs`, which `Entity.snapshot` omits when empty — so a held track with
// cameras and no attrs could never refresh (26 of 42 on the live rung A; rung B was safe only
// because every track there happens to carry `attrs.boxes`). `app.py` now emits `cameras` as
// a true per-subscriber delta, cursor advanced only on a successful send.
//
// What retiring it BUYS, beyond the bug: every client payload made the server run
// `_apply_subscribe` then `send_snapshot`, which clears `sub.prev` — so this timer was
// re-sending the entire visible world, both lanes, every 2 s per client, forever, for a
// selection that was not changing (~80 KB/s measured at base + MAX_SELECTION). A selection
// change now rides the delta like everything else.
//
// REQUIRES the app.py fix to be RUNNING, not merely committed. These two changes are one
// commit for that reason: this timer was masking the staleness, and removing a mask before
// the fix is deployed ships the bug visibly.

// ---------------------------------------------------------------- selection

/**
 * Is this track lit? Two O(1) set probes, zero allocation — this runs per track per frame.
 *
 * The CLIENT set is the sole authority, and deliberately so. `F_SELECTED` on the hot lane is
 * a pure echo of THIS connection's own subscription (`protocol.py::pack_hot` sets it from
 * `sub.selected` and from nothing else — no server-originated selection exists), so OR-ing it
 * in bought nothing and cost a round trip: selecting was instant off the local set while
 * DE-selecting waited for the echo, leaving a de-selected target lit for ~1 frame-and-a-trip.
 * The wire gate caught exactly that ("the de-selected track went dark locally", FAIL) the
 * first time the set verbs made deselect a first-class action instead of a rare edge.
 * The echo is still decoded into `Track.flags`, and the gate still asserts the round trip —
 * it is proof the server agrees, not the source of what the operator sees.
 */
function isSelectedTrack(id: number, gid: string | undefined): boolean {
  return selIds.has(id) || (gid !== undefined && selGids.has(gid))
}

/** Re-derive the anchor after a removal: the set has no order, so fall back to any member. */
function reanchor(): void {
  if (anchorGid !== null && selGids.has(anchorGid)) return
  anchorGid = null
  for (const g of selGids) { anchorGid = g; break }
  anchorId = anchorGid !== null ? (idOf(anchorGid) ?? null) : null
  if (anchorId === null && anchorGid === null) {
    for (const id of selIds) { anchorId = id; break }
  }
}

/**
 * Apply the current selection set everywhere: to the live tracks (this frame — B4 is a
 * 100 ms budget and a round trip is not free), to the published store, and to the server.
 * ONE path, so a new selection verb cannot forget one of the three.
 */
function commitSelection(): void {
  reanchor()
  pubSelection = [...selGids]
  for (const e of tracks.values()) e.selected = isSelectedTrack(e.id, e.gid)
  // The server's last verdict described the PREVIOUS selection. Clearing it to undefined
  // (= unknown) rather than leaving it is the difference between "we have not heard yet"
  // and "your new selection is fully honoured" — and only one of those is true.
  draft = {
    ...draft,
    selected: anchorId,
    selectedCount: selGids.size + selIds.size,
    selection: undefined,
  }
  publish(true)
  send(subscribeFrame())
}

// ---------------------------------------------------------------- publish (law 2)

function emit(): void {
  for (const cb of storeSubs) cb()
}

/** Publish at most every PUBLISH_MS. `now: true` bypasses the throttle (selection: B4 <=100ms). */
function publish(now = false): void {
  if (now) {
    if (publishTimer) { clearTimeout(publishTimer); publishTimer = null }
    lastPublish = performance.now()
    world = { ...draft }
    pubFlags = flags
    emit()
    return
  }
  if (publishTimer) return
  const wait = Math.max(0, PUBLISH_MS - (performance.now() - lastPublish))
  publishTimer = setTimeout(() => { publishTimer = null; publish(true) }, wait)
}

// ---------------------------------------------------------------- the hot lane

function loop(): void {
  const now = performance.now()
  for (const e of tracks.values()) {
    const a = e.span > 0 ? Math.min(1, (now - e.t0) / e.span) : 1
    e.x = e.px + (e.tx - e.px) * a
    e.y = e.py + (e.ty - e.py) * a
  }
  // Socket open but no hot frame: the view is FROZEN, not quiet — say so (word + colour).
  if (lastHot && !draft.stale && now - lastHot > STALE_MS) {
    draft = { ...draft, stale: true }
    publish(true)
  }
  if (closed) { looping = false; return }
  nextFrame(loop)
}

function onHot(buf: ArrayBuffer): void {
  const dv = new DataView(buf)
  const now = performance.now()
  // Interpolate over the OBSERVED inter-frame gap rather than a guessed constant, so the
  // motion stays smooth whether the producer broadcasts at 2.5Hz or 15Hz. Clamped so a
  // stall or a burst cannot freeze or teleport the wall.
  const span = lastHot ? Math.min(2000, Math.max(60, now - lastHot)) : 400
  lastHot = now
  if (draft.stale) { draft = { ...draft, stale: false }; publish(true) }

  seen.clear()
  for (let off = 1; off + STRIDE <= dv.byteLength; off += STRIDE) {
    const id = dv.getUint32(off, true)
    const x = dv.getFloat32(off + 4, true)
    const y = dv.getFloat32(off + 8, true)
    const fl = dv.getUint16(off + 12, true)
    seen.add(id)

    let e = tracks.get(id)
    if (e === undefined) {
      e = {
        id, kind: KINDS[fl & F_KIND] ?? 'person', x, y, flags: fl,
        lost: false, placed: false, abandoned: false, carried: false, selected: false,
        px: x, py: y, tx: x, ty: y, t0: now, span,
      }
      tracks.set(id, e)
    } else {
      // Start the new leg from where we are ON SCREEN, never from the last target — that is
      // what keeps a late or dropped frame from snapping the entity backwards.
      e.px = e.x; e.py = e.y
      e.tx = x; e.ty = y; e.t0 = now; e.span = span
    }
    e.flags = fl
    e.kind = KINDS[fl & F_KIND] ?? 'person'
    e.lost = (fl & F_LOST) !== 0
    e.placed = (fl & F_PLACED) !== 0
    e.abandoned = (fl & F_ABANDONED) !== 0
    e.carried = (fl & F_CARRIED) !== 0
    const gid = gids.get(id)
    if (gid !== undefined) e.gid = gid
    e.cameras = camerasById.get(id)
    e.attrs = attrsById.get(id)
    e.selected = isSelectedTrack(id, e.gid)
  }
  for (const id of tracks.keys()) if (!seen.has(id)) tracks.delete(id)

  // `draft.t` is the world clock of THIS hot frame: the Hub sends the control frame first,
  // then the hot frame, in the same broadcast (app.py `_send_state`), so by the time the hot
  // lane fires the matching clock is already in the draft. fe-tiles lerps against it to pin
  // boxes to `requestVideoFrameCallback` mediaTime.
  for (const cb of trackSubs) cb(tracks as Map<number, Track>, draft.t)
}

// ---------------------------------------------------------------- the control lane

interface Ctrl {
  type?: string
  t?: number
  altitude?: Altitude
  counts?: Counts
  zones?: Record<string, number>
  truncated?: boolean
  /**
   * Selection-aware truncation (CONTRACT). `truncated` is a WHOLE-SNAPSHOT bool and cannot
   * express "we honoured 200 of the 500 you pinned" — this can. Absent on a server that
   * predates the contract, which is why every reader treats absence as UNKNOWN rather than
   * as "nothing was truncated".
   */
  selection?: { requested: number; eligible: number; shown: number; dropped?: string[] }
  new?: Array<{ id: number; gid: string; kind: string; cameras?: string[]; attrs?: Record<string, unknown> }>
  lost?: number[]
  flags?: Flag[]
  /** Every group track, every broadcast (full snapshot dicts, cameras included). */
  groups?: Array<{ id: number; gid?: string; cameras?: string[]; attrs?: Record<string, unknown> }>
  /**
   * Per-frame refresh for tracks the client ALREADY holds (rung B: boxes move every
   * frame). `attrs` and `cameras` ride together deliberately — a fresh box through a
   * stale camera list would pass an outdated visibility veto. Rung A sends [].
   */
  upd?: Array<{ id: number; attrs?: Record<string, unknown>; cameras?: string[] }>
  /** Identity corrections (STACK D13): history event rows, `raw_src` -> `dst` survives. */
  id_merge?: Array<{ type: string; raw_src?: string | null; dst?: string | null }>
}

const remapSubs = new Set<(src: string, dst: string) => void>()

function onCtrl(buf: ArrayBuffer): void {
  const msg = unpack(new Uint8Array(buf, 1)) as Ctrl

  // Identity corrections FIRST: when the background tier merges the selected gid away, the
  // selection follows the surviving identity — otherwise it points at a dead gid forever
  // (the server mints a fresh dense id for the survivor, so no track ever matches again and
  // the operator's highlight silently dies while the dossier keeps fetching the old gid).
  for (const r of msg.id_merge ?? []) {
    const src = r?.raw_src
    const dst = r?.dst
    if (!src || !dst || src === dst) continue
    if (selGids.has(src)) {
      // The merged-away gid is dead: follow it to the survivor, or the highlight points at
      // a gid no track will ever carry again. A SET makes this a two-line re-key — and note
      // that src and dst can BOTH be selected, in which case the set collapses to one.
      selGids.delete(src)
      selGids.add(dst)
      if (anchorGid === src) anchorGid = dst
      anchorId = idOf(dst) ?? null   // fresh id arrives in `new` below and rebinds there
      pubSelection = [...selGids]
      send(subscribeFrame())         // re-select server-side under the surviving gid
    }
    for (const cb of remapSubs) cb(src, dst)
  }

  let upgraded = false
  for (const d of msg.new ?? []) {
    if (d?.id == null || !d.gid) continue
    gids.set(d.id, d.gid)
    const e = tracks.get(d.id)
    if (e !== undefined) e.gid = d.gid
    if (d.cameras) camerasById.set(d.id, d.cameras)
    if (d.attrs) attrsById.set(d.id, d.attrs)  // entity altitude only; zone omits attrs
    // A gid selected before its track was on screen (from a dossier or an alert) binds to a
    // hot-lane id the moment the control lane names it.
    if (d.gid === anchorGid && anchorId !== d.id) {
      anchorId = d.id
      draft = { ...draft, selected: d.id }
    }
    // A track selected by WIRE ID before its gid was known upgrades to the durable key here,
    // so it survives the next reconnect like every other member of the set.
    if (selIds.has(d.id)) {
      selIds.delete(d.id)
      selGids.add(d.gid)
      pubSelection = [...selGids]
      if (anchorId === d.id) anchorGid = d.gid
      upgraded = true
    }
  }
  // The server could not pin a target whose gid we did not know yet — `subscribeFrame`
  // had nothing to put on the wire for it. Now it does. Sent ONCE after the loop, not per
  // upgrade: a burst of `new` rows is one broadcast, and it deserves one subscribe.
  // (The 2 s selected-refresh beat would eventually correct this on its own; a target the
  // operator is following should not spend a beat unpinned and decimatable.)
  if (upgraded) send(subscribeFrame())
  for (const g of msg.groups ?? []) {
    if (g?.id != null && g.cameras) camerasById.set(g.id, g.cameras)
    if (g?.id != null && g.attrs) attrsById.set(g.id, g.attrs)
  }
  for (const u of msg.upd ?? []) {
    // Server invariant: `upd` never resurrects what `lost` retired (both derive from the
    // same filtered list). Enforce it client-side too — an unknown id must not seed
    // orphan map entries that only a socket close would reap.
    if (u?.id == null || !gids.has(u.id)) continue
    if (u.attrs) attrsById.set(u.id, u.attrs)
    if (u.cameras) camerasById.set(u.id, u.cameras)
  }
  for (const id of msg.lost ?? []) { gids.delete(id); camerasById.delete(id); attrsById.delete(id) }

  const fresh = msg.flags
  if (fresh?.length) {
    // Newest first; a single broadcast can carry several, and they arrive oldest-first.
    flags = [...fresh].reverse().concat(flags)
    if (flags.length > MAX_FLAGS) flags = flags.slice(0, MAX_FLAGS)
  }

  draft = {
    counts: msg.counts ?? draft.counts,
    zones: msg.zones ?? draft.zones,
    t: msg.t ?? draft.t,
    connected: true,
    stale: draft.stale,   // control alone is not liveness; only a hot frame clears stale
    altitude: msg.altitude ?? altitude,
    truncated: msg.truncated ?? false,
    selected: anchorId,
    selectedCount: selGids.size + selIds.size,
    // Carried forward, never defaulted: a delta that says nothing about the selection has
    // not told us the selection is intact. Absence means unknown, and only a frame that
    // actually carries the field may change what the operator is told.
    selection: msg.selection
      ? { ...msg.selection, dropped: msg.selection.dropped ?? [] }
      : draft.selection,
  }
  publish()
}

// ---------------------------------------------------------------- socket

function defaultUrl(): string {
  if (typeof location === 'undefined') {
    throw new Error('connectWorld(url) needs an explicit url outside the browser')
  }
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`
}

function subscribeFrame(): string {
  // Every selected gid goes on the wire. The server pins them ALL at rank 0 in decimation
  // (`world.get_snapshot(pin=...)`), which is what makes "highlight hundreds at once"
  // survive a crowd larger than the snapshot cap. Ids selected before their gid was named
  // are resolved here rather than dropped.
  const ids: string[] = [...selGids]
  for (const id of selIds) {
    const g = gids.get(id)
    if (g !== undefined && !selGids.has(g)) ids.push(g)
  }
  return JSON.stringify({ viewport, altitude, selected_ids: ids })
}

function send(payload: string): boolean {
  if (sock?.readyState !== WebSocket.OPEN) return false
  sock.send(payload)
  return true
}

function scheduleRetry(): void {
  if (closed || retryTimer) return
  // Exponential backoff with jitter, so a restarted backend is not hit by a thundering herd
  // of reconnecting warroom tabs.
  const base = Math.min(RETRY_MAX, RETRY_MIN * 2 ** attempt)
  const wait = base * (0.7 + Math.random() * 0.6)
  attempt += 1
  retryTimer = setTimeout(() => { retryTimer = null; open() }, wait)
}

function open(): void {
  if (closed) return
  const ws = new WebSocket(wsUrl)
  ws.binaryType = 'arraybuffer'
  sock = ws

  ws.onopen = () => {
    attempt = 0
    ws.send(subscribeFrame())
  }

  ws.onmessage = (ev: MessageEvent) => {
    const buf = ev.data as ArrayBuffer
    if (!(buf instanceof ArrayBuffer) || buf.byteLength === 0) return
    const tag = new Uint8Array(buf, 0, 1)[0]
    if (tag === TAG_HOT) onHot(buf)
    else if (tag === TAG_CTRL) onCtrl(buf)
  }

  ws.onerror = () => { try { ws.close() } catch { /* already gone */ } }

  ws.onclose = () => {
    if (sock === ws) sock = null
    lastHot = 0
    // Wire ids are minted per HUB lifetime (`app.py::Hub._ids`/`_next`), so a restarted
    // backend re-issues them from 1: every id->gid pair from the dead connection is a lie,
    // and a `selected` wire id left pinned lands the operator's highlight on whatever
    // unrelated entity inherits that number. `selGids` is the reconnect-durable identity
    // and stays — all of it, however large; the id mirror is transport detail and dies with
    // the connection. Anchor re-binds in onCtrl's `new` loop when the gid is named again.
    gids.clear()
    camerasById.clear()
    attrsById.clear()
    selIds.clear()
    anchorId = null
    draft = { ...draft, connected: false, stale: false, selected: null, selection: undefined }
    publish(true)
    scheduleRetry()
  }
}

// ================================================================ public contract

/** Open the live world socket. Idempotent — calling it twice does not open a second socket. */
export function connectWorld(url?: string): void {
  closed = false
  if (sock && (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING)) return
  if (!looping) { looping = true; nextFrame(loop) }
  // STATIC MODE (airsec.akeyo.io): there is no socket to open — a recorded session is fed
  // to the SAME two handlers, in the same order, at its own recorded pace. Everything
  // downstream of `onHot`/`onCtrl` cannot tell the difference, which is the point: the
  // published warroom runs the shipped decoder on the shipped protocol.
  if (isStatic()) { startReplay(); return }
  wsUrl = url ?? (wsUrl || defaultUrl())
  // A reconnect already scheduled would open a SECOND socket behind this one.
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null }
  open()
}

// ---------------------------------------------------------------- recorded playback

let replayTimer: ReturnType<typeof setTimeout> | null = null

/** Feed the recorded frames on their own timeline, looping seamlessly at the end. */
function startReplay(): void {
  if (replayTimer) return
  void loadRecording().then((rec) => {
    draft = { ...draft, connected: true, stale: false }
    publish(true)
    let i = 0
    let lap = 0                                  // ms added by completed loops
    const t0 = performance.now()
    const step = (): void => {
      if (closed) { replayTimer = null; return }
      const now = performance.now() - t0
      // Deliver every frame whose recorded moment has passed — a backgrounded tab wakes up
      // behind, and skipping to "now" would tear the world; the wall lerps over the catch-up.
      while (i < rec.frames.length && rec.frames[i][0] + lap <= now) {
        const [, off, len] = rec.frames[i]
        const buf = (rec.bytes.buffer as ArrayBuffer).slice(
          rec.bytes.byteOffset + off, rec.bytes.byteOffset + off + len)
        const tag = new Uint8Array(buf, 0, 1)[0]
        if (tag === TAG_HOT) onHot(buf)
        else if (tag === TAG_CTRL) onCtrl(buf)
        i += 1
      }
      setReplayClock(now, rec.mediaT0, rec.durationS)
      if (i >= rec.frames.length) {
        // The pack itself loops at `durationS`; restarting the recording at the same
        // instant keeps the videos and the world model in step across the seam.
        i = 0
        lap += rec.msTotal
      }
      replayTimer = setTimeout(step, 40)
    }
    step()
  }).catch(() => {
    // No bundle: say disconnected rather than pretending. The wall's own empty state
    // then tells the visitor what is wrong, in its own words.
    draft = { ...draft, connected: false, stale: true }
    publish(true)
  })
}

/** Close the socket and stop reconnecting (test harnesses, hot-module teardown). */
export function disconnectWorld(): void {
  closed = true
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null }
  try { sock?.close() } catch { /* already gone */ }
  sock = null
  tracks.clear()
}

/**
 * Subscribe to hot-lane frames. The callback receives the LIVE map (hold the reference and
 * read it from your own rAF loop; do not copy it per frame) plus the world clock `t` of the
 * frame, for lerping against `requestVideoFrameCallback` mediaTime. Fires once immediately
 * with the current set once a frame has landed on this connection, so a late subscriber is
 * never blank. Returns an unsubscribe function.
 */
export function subscribeTracks(cb: (tracks: Map<number, Track>, t: number) => void): () => void {
  trackSubs.add(cb)
  // Replay only after a real frame: before that `draft.t` is 0 and the map is empty, which a
  // consumer cannot tell apart from a world at t=0 with zero entities (fe-tiles seeds its
  // time base from this value). `lastHot` clears on close, so a subscriber that mounts inside
  // a disconnect window waits for the reconnect rather than being handed a frozen crowd.
  if (lastHot) cb(tracks as Map<number, Track>, draft.t)
  return () => { trackSubs.delete(cb) }
}

/** Re-subscribe at a new viewport/altitude. Backend decimation follows this, not the client. */
export function subscribe(opts: { viewport?: Viewport | null; altitude?: Altitude }): void {
  if ('viewport' in opts) viewport = opts.viewport ?? null
  if (opts.altitude) altitude = opts.altitude
  send(subscribeFrame())
}

/** Non-hook read of the throttled world store (canvas/imperative code, tests). */
export function getWorld(): WorldSnapshot { return world }

/** Non-hook read of the graded flag list, newest-ARRIVED first (see useFlags). */
export function getFlags(): Flag[] { return pubFlags }

function subscribeStore(cb: () => void): () => void {
  storeSubs.add(cb)
  return () => { storeSubs.delete(cb) }
}

/** Throttled (<=10Hz) world structure. Referentially stable between publishes. */
export function useWorldSnapshot(): WorldSnapshot {
  return useSyncExternalStore(subscribeStore, getWorld, getWorld)
}

/**
 * Graded rule-engine findings, newest-ARRIVED first (each control batch prepends).
 * Referentially stable until a new flag lands. NOTE: `t` is NOT monotonic down the list —
 * a pattern may conclude late about an earlier moment (dwell, retro sweeps), so a rail
 * that wants strict timestamp order must sort by `t` itself; arrival order is what an
 * operator scanning "what just fired" wants.
 */
export function useFlags(): Flag[] {
  return useSyncExternalStore(subscribeStore, getFlags, getFlags)
}

/** World-model gid for a hot-lane id, once the control lane has named it. */
export function gidOf(id: number): string | undefined {
  return gids.get(id)
}

/** Reverse lookup: hot-lane id for a world-model gid. */
export function idOf(gid: string): number | undefined {
  for (const [id, g] of gids) if (g === gid) return id
  return undefined
}

/**
 * Select an entity (the vision's universal verb). Canonical form takes a GID — the identity
 * that survives reconnects; the server's own selection key is the gid (`_apply_subscribe`).
 * A hot-lane id is accepted for convenience and resolved internally, but wire ids are
 * reassigned per connection, so panels should pass the gid.
 *
 * Applied LOCALLY first so the highlight is on screen this frame (B4: <=100ms), then told to
 * the server, which mirrors it back as `F_SELECTED` on the hot lane and re-sends a snapshot.
 *
 * This is the SINGLE-target verb and it REPLACES the whole selection — the behaviour every
 * existing panel was written against. The set verbs beside it (`selectMany`, `addSelection`,
 * `toggleSelected`, `removeSelection`, `clearSelection`) are how the operator gets to
 * hundreds at once.
 */
export function select(target: string | number | null): void {
  selectOnly(target)
}

/** Add one target to the selection set without disturbing the rest. Returns the resolved gid. */
function addOne(target: string | number): string | null {
  if (typeof target === 'number' && (import.meta as any).env?.DEV) {
    console.warn(
      `select(${target}) received a wire id; wire ids are reassigned on reconnect — pass the gid.`,
    )
  }
  const gid = typeof target === 'string' ? target : (gids.get(target) ?? null)
  if (gid !== null) {
    selGids.add(gid)
    anchorGid = gid
    anchorId = idOf(gid) ?? (typeof target === 'number' ? target : null)
  } else if (typeof target === 'number') {
    // No gid yet: hold the wire id and upgrade in onCtrl the moment the control lane names it.
    selIds.add(target)
    anchorId = target
  }
  return gid
}

/** Replace the whole selection with exactly this one target (or clear it). */
export function selectOnly(target: string | number | null): void {
  if (target === null) { clearSelection(); return }
  const gid = typeof target === 'string' ? target : (gids.get(target) ?? null)
  // Already exactly this, alone — nothing to do. (The dossier's retry path depends on this
  // short-circuit being narrow: it is only a no-op when the SET is already the singleton.)
  if (gid !== null && selGids.size === 1 && selIds.size === 0 && selGids.has(gid)) return
  selGids.clear()
  selIds.clear()
  anchorGid = null
  anchorId = null
  addOne(target)
  commitSelection()
}

/**
 * Replace the selection with this whole set — the marquee / "select all in zone" verb.
 * Hundreds is the design point, not the edge case: the server takes the full set and pins
 * every one of them at rank 0 in decimation.
 */
export function selectMany(targets: Iterable<string | number>): void {
  selGids.clear()
  selIds.clear()
  anchorGid = null
  anchorId = null
  for (const t of targets) addOne(t)
  commitSelection()
}

/** Union this set INTO the selection (shift-click, additive marquee). */
export function addSelection(targets: Iterable<string | number>): void {
  const before = selGids.size + selIds.size
  for (const t of targets) addOne(t)
  if (selGids.size + selIds.size === before) return
  commitSelection()
}

/** Remove these from the selection (shift-drag over already-lit targets). */
export function removeSelection(targets: Iterable<string>): void {
  let changed = false
  for (const g of targets) if (selGids.delete(g)) changed = true
  if (!changed) return
  commitSelection()
}

/** Toggle one target — the shift-click primitive. */
export function toggleSelected(target: string | number): void {
  const gid = typeof target === 'string' ? target : (gids.get(target) ?? null)
  if (gid !== null && selGids.has(gid)) {
    selGids.delete(gid)
    if (anchorGid === gid) anchorGid = null
    commitSelection()
    return
  }
  addOne(target)
  commitSelection()
}

/** Clear the whole selection. */
export function clearSelection(): void {
  if (selGids.size === 0 && selIds.size === 0) return
  selGids.clear()
  selIds.clear()
  anchorGid = null
  anchorId = null
  commitSelection()
}

/** Hot-lane id of the ANCHOR, or null. Null with a live `selectedGid()` means not on screen. */
export function selectedId(): number | null { return anchorId }

/** NON-reactive ANCHOR gid — for rAF render loops. React panels use `useSelected()`. */
export function selectedGid(): string | null { return anchorGid }

/**
 * NON-reactive live selection SET — for rAF render loops (the map reads this every frame).
 * Held by reference and mutated in place, exactly like `liveTracks()`: never copy it per frame.
 */
export function selectedGidSet(): ReadonlySet<string> { return selGids }

/** How many targets are selected right now (including any not yet resolved to a gid). */
export function selectionSize(): number { return selGids.size + selIds.size }

function getSelectedGid(): string | null { return anchorGid }
function getSelection(): readonly string[] { return pubSelection }

/** REACTIVE anchor gid — re-renders the consuming panel when the anchor changes. */
export function useSelected(): string | null {
  return useSyncExternalStore(subscribeStore, getSelectedGid, getSelectedGid)
}

/** REACTIVE selection set, referentially stable between changes. */
export function useSelection(): readonly string[] {
  return useSyncExternalStore(subscribeStore, getSelection, getSelection)
}

/**
 * Subscribe to identity corrections (id_merge/id_split): `cb(oldGid, survivingGid)`.
 * A panel holding per-gid state (a dossier fetch, a trail cache) re-keys on this signal.
 */
export function subscribeRemaps(cb: (src: string, dst: string) => void): () => void {
  remapSubs.add(cb)
  return () => { remapSubs.delete(cb) }
}

/** Live tracks by hot-lane id. Same map `subscribeTracks` hands out — stable, mutated in place. */
export function liveTracks(): Map<number, Track> { return tracks as Map<number, Track> }
